import React, { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Link2, Loader2, CheckCircle2, XCircle, RefreshCw, DownloadCloud, Activity, HelpCircle, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const EMPTY = {
  enabled: false, base_url: '', email: '', api_token: '', project_key: '',
  server_id: '', bug_issue_type: 'Bug', task_issue_type: 'Task',
};

const STATUS_ROWS = [
  ['open', 'Open'],
  ['in_progress', 'In Progress'],
  ['resolved', 'Resolved'],
  ['closed', 'Closed'],
];

const FIELD_HELP = [
  ['Jira Base URL', 'Your Jira Cloud site address, e.g. https://your-org.atlassian.net. For Jira Server/Data Center use the full host URL. No trailing slash needed.'],
  ['Account Email', 'The Atlassian account email that owns the API token below. Best practice: a dedicated service account (e.g. svc-jira@your-org.com) rather than a personal login.'],
  ['API Token', 'An Atlassian API token (not your password). Create one at id.atlassian.com → Security → "Create and manage API tokens". Paste it once; it is stored encrypted and never shown again.'],
  ['Project Key', 'The short key of the Jira project that should receive new issues, e.g. SUP or OPS. You can see it in the Jira project URL (…/browse/SUP-123).'],
  ['Server / Install ID', 'A free-text label for THIS install (e.g. prod-eu). It only tags created issues with an inzara_srv_<id> label so you can tell environments apart in Jira — it does not affect the connection.'],
  ['Bug Issue Type', 'The Jira issue type used when a user submits a bug (default "Bug"). It must exist in the chosen project\'s issue-type scheme — spelling must match exactly.'],
  ['Feature Issue Type', 'The Jira issue type used for feature requests (default "Task"). Must also exist in the project\'s scheme.'],
  ['Status mapping', 'For each feedback state (Open / In Progress / Resolved / Closed), list the Jira status name(s) that mean the same thing, comma-separated (e.g. Resolved → "Done, Resolved"). When Jira is polled, an issue\'s status is mapped back to the feedback state. Matching is case-insensitive; a status already listed under the current state is left as-is to avoid resolved↔closed flapping.'],
];

function JiraSetupGuide({ open, onOpenChange, serverId }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-auto" data-testid="jira-setup-guide-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Link2 className="w-4 h-4 text-primary" />Connect Jira — setup guide</DialogTitle>
          <DialogDescription className="text-xs">
            This is a server-wide connection shared by all tenants on this install. New feedback becomes a Jira issue;
            Jira status &amp; comment changes are polled back every 15 minutes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-xs">
          <section className="space-y-1.5">
            <h4 className="font-semibold text-sm">1 · In Jira (do this first)</h4>
            <ol className="list-decimal pl-4 space-y-1 text-muted-foreground">
              <li>Create (or pick) the <strong>project</strong> that will hold the issues and note its <strong>key</strong> (e.g. SUP).</li>
              <li>Make sure the project's issue-type scheme includes the types you'll use for bugs and features (defaults <code>Bug</code> and <code>Task</code>).</li>
              <li>Create an <strong>API token</strong> at{' '}
                <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer" className="text-primary inline-flex items-center gap-0.5">id.atlassian.com → API tokens <ExternalLink className="w-3 h-3" /></a>{' '}
                while signed in as the service account.</li>
              <li>Confirm that account has <strong>Create issues</strong>, <strong>Add comments</strong> and <strong>Transition issues</strong> permissions in the project (Project settings → Permissions).</li>
              <li>Note the exact <strong>status names</strong> in your workflow (e.g. To Do, In Progress, Done) so you can map them below.</li>
            </ol>
          </section>
          <section className="space-y-1.5">
            <h4 className="font-semibold text-sm">2 · What each field means</h4>
            <dl className="space-y-2">
              {FIELD_HELP.map(([label, desc]) => (
                <div key={label}>
                  <dt className="font-medium text-foreground">{label}</dt>
                  <dd className="text-muted-foreground">{desc}</dd>
                </div>
              ))}
            </dl>
          </section>
          <section className="space-y-1.5">
            <h4 className="font-semibold text-sm">3 · Finish &amp; verify</h4>
            <ol className="list-decimal pl-4 space-y-1 text-muted-foreground">
              <li>Fill the fields, then click <strong>Test connection</strong> — it should report "Connected as …" with your project name.</li>
              <li>Flip the <strong>enable</strong> switch on to start auto-creating issues from new feedback.</li>
              <li>Use <strong>Backfill</strong> to push existing feedback that isn't in Jira yet, and <strong>Poll now</strong> to pull status changes immediately instead of waiting 15 minutes.</li>
              <li>Created issues are tagged <code>inzara</code>, <code>inzara_srv_{serverId ? serverId.toLowerCase().replace(/[^a-z0-9]+/g, '_') : '<id>'}</code> and a per-tenant <code>inzara_tenant_&lt;id&gt;</code> label so you can filter them in Jira.</li>
            </ol>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}


export const JiraSettingsCard = () => {
  const [form, setForm] = useState(EMPTY);
  const [statusMap, setStatusMap] = useState({ open: '', in_progress: '', resolved: '', closed: '' });
  const [meta, setMeta] = useState({ api_token_set: false, api_token_hint: '', last_poll_at: null, ready: false });
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const pollTimer = useRef(null);

  const applyCfg = (cfg) => {
    setForm({
      enabled: !!cfg.enabled, base_url: cfg.base_url || '', email: cfg.email || '',
      api_token: '', project_key: cfg.project_key || '', server_id: cfg.server_id || '',
      bug_issue_type: cfg.bug_issue_type || 'Bug', task_issue_type: cfg.task_issue_type || 'Task',
    });
    const sm = cfg.status_map || {};
    setStatusMap({
      open: (sm.open || []).join(', '), in_progress: (sm.in_progress || []).join(', '),
      resolved: (sm.resolved || []).join(', '), closed: (sm.closed || []).join(', '),
    });
    setMeta({ api_token_set: !!cfg.api_token_set, api_token_hint: cfg.api_token_hint || '', last_poll_at: cfg.last_poll_at || null, ready: !!cfg.ready });
  };

  const loadHealth = useCallback(async () => {
    try { setHealth(await api.getJiraHealth()); } catch { /* non-fatal */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = await api.getJiraConfig();
      applyCfg(cfg);
      await loadHealth();
    } catch (e) { toast.error(e.message || 'Could not load Jira config'); }
    finally { setLoading(false); }
  }, [loadHealth]);

  useEffect(() => { load(); }, [load]);

  // Poll health while a backfill is running.
  useEffect(() => {
    const running = health?.backfill?.running;
    if (running && !pollTimer.current) {
      pollTimer.current = setInterval(loadHealth, 2500);
    } else if (!running && pollTimer.current) {
      clearInterval(pollTimer.current); pollTimer.current = null;
    }
    return () => { if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; } };
  }, [health?.backfill?.running, loadHealth]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const buildPayload = (withToken = true) => {
    const payload = { ...form, status_map: { ...statusMap } };
    if (withToken && !payload.api_token) delete payload.api_token;
    if (!withToken) delete payload.api_token;
    return payload;
  };

  const save = async () => {
    setSaving(true);
    try {
      const cfg = await api.saveJiraConfig(buildPayload());
      applyCfg(cfg);
      toast.success('Jira configuration saved');
    } catch (e) { toast.error(e.message || 'Could not save'); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await api.testJiraConnection(buildPayload());
      setTestResult(res);
      if (res.ok) toast.success(`Connected as ${res.account || 'Jira user'}`);
      else toast.error(res.error || 'Connection failed');
    } catch (e) { setTestResult({ ok: false, error: e.message }); toast.error(e.message || 'Connection failed'); }
    finally { setTesting(false); }
  };

  const pollNow = async () => {
    setPolling(true);
    try {
      const res = await api.pollJira();
      if (res.ok) toast.success(`Polled ${res.polled} issue(s), updated ${res.updated}`);
      else toast.error(res.reason || 'Poll failed');
      loadHealth();
    } catch (e) { toast.error(e.message || 'Poll failed'); }
    finally { setPolling(false); }
  };

  const backfill = async () => {
    setBackfilling(true);
    try {
      const res = await api.backfillJira();
      if (res.ok && res.already_running) toast.info('A backfill is already running');
      else if (res.ok) toast.success('Backfill started — syncing existing feedback to Jira');
      else toast.error(res.error || 'Could not start backfill');
      await loadHealth();
    } catch (e) { toast.error(e.message || 'Could not start backfill'); }
    finally { setBackfilling(false); }
  };

  const bf = health?.backfill;
  const lpr = health?.last_poll_result;

  return (
    <div className="glass-card rounded-2xl p-6 space-y-4 animate-slide-up" style={{ animationDelay: '135ms' }} data-testid="jira-settings-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link2 className="w-5 h-5 text-primary" strokeWidth={1.5} />
          <h2 className="text-base font-heading font-semibold text-foreground">Jira Integration</h2>
          {meta.ready && <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-400 bg-emerald-500/10">Ready</Badge>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={() => setGuideOpen(true)} data-testid="jira-setup-guide-btn">
            <HelpCircle className="w-3.5 h-3.5" />How to set up
          </Button>
          <Switch checked={form.enabled} onCheckedChange={v => set('enabled', v)} data-testid="jira-enabled-switch" />
        </div>
      </div>

      <JiraSetupGuide open={guideOpen} onOpenChange={setGuideOpen} serverId={form.server_id} />
      <Separator className="bg-border/50" />
      <p className="text-xs text-muted-foreground/80">
        Server-wide connection shared by all tenants on this install. New feedback is auto-created as a Jira issue, tagged
        with labels <code className="text-foreground/80">inzara</code>, <code className="text-foreground/80">inzara_srv_{form.server_id ? form.server_id.toLowerCase().replace(/[^a-z0-9]+/g, '_') : '<id>'}</code> and a per-tenant <code className="text-foreground/80">inzara_tenant_&lt;id&gt;</code> label.
        Status &amp; comment changes are polled back every 15 minutes.
      </p>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="col-span-2">
            <Label className="text-muted-foreground text-xs mb-1">Jira Base URL</Label>
            <Input value={form.base_url} onChange={e => set('base_url', e.target.value)} placeholder="https://your-org.atlassian.net" className="h-8 text-sm" data-testid="jira-base-url-input" />
          </div>
          <div>
            <Label className="text-muted-foreground text-xs mb-1">Account Email</Label>
            <Input value={form.email} onChange={e => set('email', e.target.value)} placeholder="svc-account@your-org.com" className="h-8 text-sm" data-testid="jira-email-input" />
          </div>
          <div>
            <Label className="text-muted-foreground text-xs mb-1">API Token {meta.api_token_set && <span className="text-emerald-400/80">· saved {meta.api_token_hint}</span>}</Label>
            <Input type="password" value={form.api_token} onChange={e => set('api_token', e.target.value)} placeholder={meta.api_token_set ? 'Leave blank to keep current' : 'Atlassian API token'} className="h-8 text-sm" data-testid="jira-token-input" />
          </div>
          <div>
            <Label className="text-muted-foreground text-xs mb-1">Project Key</Label>
            <Input value={form.project_key} onChange={e => set('project_key', e.target.value.toUpperCase())} placeholder="SUP" className="h-8 text-sm" data-testid="jira-project-key-input" />
          </div>
          <div>
            <Label className="text-muted-foreground text-xs mb-1">Server / Install ID</Label>
            <Input value={form.server_id} onChange={e => set('server_id', e.target.value)} placeholder="prod-eu" className="h-8 text-sm" data-testid="jira-server-id-input" />
          </div>
          <div>
            <Label className="text-muted-foreground text-xs mb-1">Bug Issue Type</Label>
            <Input value={form.bug_issue_type} onChange={e => set('bug_issue_type', e.target.value)} placeholder="Bug" className="h-8 text-sm" data-testid="jira-bug-type-input" />
          </div>
          <div>
            <Label className="text-muted-foreground text-xs mb-1">Feature Issue Type</Label>
            <Input value={form.task_issue_type} onChange={e => set('task_issue_type', e.target.value)} placeholder="Task" className="h-8 text-sm" data-testid="jira-task-type-input" />
          </div>
        </div>
      )}

      {/* Status mapping */}
      {!loading && (
        <div className="space-y-2" data-testid="jira-status-map">
          <Separator className="bg-border/50" />
          <Label className="text-muted-foreground text-xs">Status mapping — Jira status names for each feedback state (comma-separated)</Label>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {STATUS_ROWS.map(([key, label]) => (
              <div key={key}>
                <Label className="text-muted-foreground text-[11px] mb-1">{label}</Label>
                <Input
                  value={statusMap[key]}
                  onChange={e => setStatusMap(p => ({ ...p, [key]: e.target.value }))}
                  placeholder="e.g. Done, Resolved"
                  className="h-8 text-sm"
                  data-testid={`jira-statusmap-${key}-input`}
                />
              </div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground/60">Matching is case-insensitive. A Jira status listed under the current feedback state is left untouched, which prevents resolved ↔ closed flapping.</p>
        </div>
      )}

      {testResult && (
        <div className={`text-xs flex items-center gap-1.5 ${testResult.ok ? 'text-emerald-400' : 'text-rose-400'}`} data-testid="jira-test-result">
          {testResult.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
          {testResult.ok ? `Connected as ${testResult.account || 'Jira user'}${testResult.project_name ? ` · project "${testResult.project_name}"` : ''}` : (testResult.error || 'Connection failed')}
        </div>
      )}

      {/* Sync health */}
      {health && (
        <div className="rounded-xl border border-border/50 bg-background/30 p-4 space-y-3" data-testid="jira-health-panel">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary" strokeWidth={1.5} />
            <span className="text-sm font-medium text-foreground">Sync health</span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center" data-testid="jira-health-synced">
              <div className="text-xl font-heading font-semibold text-emerald-400">{health.synced}</div>
              <div className="text-[11px] text-muted-foreground/70">Synced</div>
            </div>
            <div className="text-center" data-testid="jira-health-pending">
              <div className="text-xl font-heading font-semibold text-amber-400">{health.pending}</div>
              <div className="text-[11px] text-muted-foreground/70">Pending</div>
            </div>
            <div className="text-center" data-testid="jira-health-failed">
              <div className={`text-xl font-heading font-semibold ${health.failed ? 'text-rose-400' : 'text-foreground/60'}`}>{health.failed}</div>
              <div className="text-[11px] text-muted-foreground/70">Failed</div>
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground/70 space-y-0.5">
            <div data-testid="jira-health-lastpoll">
              {meta.last_poll_at ? `Last poll: ${new Date(meta.last_poll_at).toLocaleString()}` : 'Not polled yet'}
              {lpr && typeof lpr.updated === 'number' ? ` · ${lpr.polled} scanned, ${lpr.updated} updated` : ''}
              {lpr && lpr.ok === false ? ` · error: ${lpr.error || 'poll failed'}` : ''}
            </div>
            {bf && (
              <div data-testid="jira-health-backfill">
                {bf.running
                  ? `Backfill running… ${bf.done}/${bf.total} processed (${bf.created} created, ${bf.failed} failed)`
                  : bf.finished_at
                    ? `Last backfill: ${bf.created} created, ${bf.failed} failed of ${bf.total} · ${new Date(bf.finished_at).toLocaleString()}`
                    : null}
              </div>
            )}
          </div>
          <Button
            variant="outline" size="sm"
            className="rounded-lg text-xs h-8 gap-1.5 w-full"
            onClick={backfill}
            disabled={backfilling || !meta.ready || bf?.running}
            data-testid="jira-backfill-btn"
          >
            {backfilling || bf?.running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <DownloadCloud className="w-3.5 h-3.5" />}
            {bf?.running ? 'Backfill in progress…' : 'Sync all existing feedback to Jira'}
          </Button>
        </div>
      )}

      <div className="flex items-center justify-end pt-1">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-lg text-xs h-8 gap-1.5" onClick={pollNow} disabled={polling || !meta.ready} data-testid="jira-poll-btn">
            {polling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Poll now
          </Button>
          <Button variant="outline" size="sm" className="rounded-lg text-xs h-8 gap-1.5" onClick={test} disabled={testing} data-testid="jira-test-btn">
            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Test connection
          </Button>
          <Button size="sm" className="rounded-lg text-xs h-8 gap-1.5" onClick={save} disabled={saving} data-testid="jira-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default JiraSettingsCard;
